/**
 * The one-turn model (MIN-297). `Turn`/`TurnPart`/`ToolPart`/`TurnOutcome`
 * replace the package's split `ChatMessage.toolCalls` (payloads) /
 * `ChatMessage.tools` (names only) encodings — the split that let a mapper
 * pick the lossy one (MIN-291).
 *
 * A `Turn` is produced by exactly one internal builder (`buildTurn`), fed by
 * exactly two entry points: `turnFromHistory` (persisted `EngineMessage`) and
 * `turnFromStream` (the live AI SDK stream) — so a field one path writes and
 * the other drops is a bug in one function, caught by one test (the parity
 * law in `__tests__/turn.test.ts`).
 */
import type { UIMessage } from "ai";
import type { EngineMessage } from "./message";

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

/** A settled tool call cannot typecheck without its payload. */
export type ToolPart =
  | { kind: "tool"; callId: string; name: string; state: "running"; input?: unknown }
  | { kind: "tool"; callId: string; name: string; state: "done"; input: unknown; output: unknown }
  | { kind: "tool"; callId: string; name: string; state: "error"; input: unknown; error: string };

export type TurnPart =
  | { kind: "text"; text: string }
  | { kind: "reasoning"; text: string }
  | ToolPart
  | { kind: "citation"; id: string; title: string; detail?: string; excerpt?: string; score?: number }
  /** Host extension point. The package ships it byte-for-byte to a registered
   * renderer and never interprets it — this is how a Recommendation card
   * reaches the UI without agent-chat learning what a pyramid is. */
  | { kind: "opaque"; renderAs: string; data: unknown };

/** REQUIRED. Not optional metadata anyone can forget to copy. */
export type TurnOutcome = "streaming" | "complete" | "interrupted" | "failed";

export interface Turn {
  readonly id: string;
  readonly role: "user" | "assistant";
  readonly parts: readonly TurnPart[];
  readonly outcome: TurnOutcome;
  readonly usage?: TokenUsage;
}

/** One RAG hit, whichever raw shape it arrived in (persisted `EngineRagResult`
 * or the live stream's tool output — both use the same field names). */
interface RagResult {
  source_uri: string;
  source_display_name?: string | null;
  page_span?: number[] | null;
  chunk_text?: string | null;
  score?: number | null;
}

function isRagResultArray(value: unknown): value is RagResult[] {
  return Array.isArray(value) && value.every((v) => typeof v === "object" && v !== null && "source_uri" in v);
}

/** Mirror of the engine's `_format_page_span`: [first, last] → "p. 12" / "pp. 45–47". */
function formatPageSpan(span?: number[] | null): string | undefined {
  if (!span || span.length === 0) return undefined;
  const first = span[0];
  const last = span[span.length - 1];
  return first === last ? `p. ${first}` : `pp. ${first}–${last}`;
}

/** A tool call, normalized to one shape regardless of which source (persisted
 * history or the live stream) it came from — the input the shared builder
 * assembles parts from. */
interface RawToolCall {
  callId: string;
  name: string;
  input: unknown;
  /** Whether this call settled — false leaves the tool part `running`,
   * distinguishing "nothing recorded yet" from a settled call with an empty
   * payload (`args: {}` is a recorded empty argument set, not an absent one). */
  hasOutput: boolean;
  output?: unknown;
  /** Set only by the stream path — a failed AI SDK tool call's `errorText`
   * (`state: "output-error"`). The history path can't produce one:
   * `EngineToolCall` (citations.ts) carries only `tool_name`/`args`/`result`. */
  errorText?: string;
}

function toolPartFromRaw(raw: RawToolCall): ToolPart {
  if (raw.errorText !== undefined) {
    return { kind: "tool", callId: raw.callId, name: raw.name, state: "error", input: raw.input, error: raw.errorText };
  }
  if (!raw.hasOutput) {
    return {
      kind: "tool",
      callId: raw.callId,
      name: raw.name,
      state: "running",
      ...(raw.input !== undefined ? { input: raw.input } : {}),
    };
  }
  return { kind: "tool", callId: raw.callId, name: raw.name, state: "done", input: raw.input, output: raw.output };
}

function citationPartsFromRaw(raw: RawToolCall, startIndex: number): Extract<TurnPart, { kind: "citation" }>[] {
  if (!raw.hasOutput || !isRagResultArray(raw.output)) return [];
  return raw.output.map((r, i) => ({
    kind: "citation" as const,
    id: `cite-${startIndex + i}`,
    title: r.source_display_name || r.source_uri,
    detail: formatPageSpan(r.page_span),
    excerpt: r.chunk_text ?? undefined,
    score: r.score ?? undefined,
  }));
}

interface BuildTurnInput {
  id: string;
  role: "user" | "assistant";
  reasoning?: string;
  text?: string;
  toolCalls: RawToolCall[];
  outcome: TurnOutcome;
  usage?: TokenUsage;
}

/** The one internal builder. Both `turnFromHistory` and `turnFromStream`
 * normalize their source into this shape and delegate here — so a field one
 * source writes and the other drops is a bug in this one function. */
function buildTurn(input: BuildTurnInput): Turn {
  const parts: TurnPart[] = [];
  if (input.reasoning) parts.push({ kind: "reasoning", text: input.reasoning });

  const toolParts = input.toolCalls.map(toolPartFromRaw);
  const citationParts: Extract<TurnPart, { kind: "citation" }>[] = [];
  for (const raw of input.toolCalls) {
    citationParts.push(...citationPartsFromRaw(raw, citationParts.length));
  }

  parts.push(...toolParts);
  if (input.text) parts.push({ kind: "text", text: input.text });
  parts.push(...citationParts);

  return {
    id: input.id,
    role: input.role,
    parts,
    outcome: input.outcome,
    ...(input.usage ? { usage: input.usage } : {}),
  };
}

let callIdSeq = 0;
function nextCallId(): string {
  callIdSeq += 1;
  return `call-${callIdSeq}`;
}

/** Resets `nextCallId`'s counter. Test-only: without it, tests that don't
 * pin a concrete `callId` still couple to each other through import/run
 * order. */
export function resetCallIdSequence(): void {
  callIdSeq = 0;
}

let fallbackMessageIdSeq = 0;
/** The message-id fallback used when `EngineMessage.id` is absent — its own
 * counter, separate from `nextCallId`'s tool-call-id namespace (PR #547
 * review item 5: one counter serving two id namespaces coupled unrelated
 * tests through import order). */
function nextFallbackMessageId(): string {
  fallbackMessageIdSeq += 1;
  return `turn-${fallbackMessageIdSeq}`;
}

/** Builds a `Turn` from a persisted `EngineMessage` (a reloaded thread's history). */
export function turnFromHistory(engine: EngineMessage): Turn {
  const toolCalls: RawToolCall[] = (engine.tool_calls ?? []).map((tc) => ({
    callId: nextCallId(),
    name: tc.tool_name,
    input: tc.args,
    hasOutput: tc.result !== undefined,
    output: tc.result,
  }));

  return buildTurn({
    id: engine.id !== null && engine.id !== undefined ? String(engine.id) : nextFallbackMessageId(),
    text: engine.content || undefined,
    role: engine.role,
    reasoning: engine.role === "assistant" ? (engine.reasoning ?? undefined) : undefined,
    toolCalls,
    outcome: engine.interrupted ? "interrupted" : "complete",
    usage: engine.usage
      ? {
          inputTokens: engine.usage.input_tokens,
          outputTokens: engine.usage.output_tokens,
          requests: engine.usage.requests,
        }
      : undefined,
  });
}

/** An AI SDK v5 `UIMessage`-shaped part, as received from the live stream. */
interface StreamPart {
  type: string;
  text?: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  /** Set when `state` is the AI SDK's `"output-error"` (see
   * node_modules/ai/dist/index.d.ts) — the failed call's error message. */
  errorText?: string;
}

/** A live-stream message, in the shape `turnFromStream` consumes. */
export interface StreamTurnMessage {
  id: string;
  role: "user" | "assistant";
  parts: StreamPart[];
  metadata?: { interrupted?: boolean; usage?: TokenUsage };
}

export interface StreamTurnInput {
  message: StreamTurnMessage;
  /** Whether the stream reached a terminal event. `false` means it ended
   * (disconnected, errored) without one — never a silent `"complete"`. */
  terminal: boolean;
}

/** Builds a `Turn` from the live AI SDK stream. */
export function turnFromStream(input: StreamTurnInput): Turn {
  const { message } = input;
  let reasoning = "";
  let text = "";
  const toolCalls: RawToolCall[] = [];

  for (const part of message.parts) {
    if (part.type === "reasoning") {
      reasoning += part.text ?? "";
    } else if (part.type === "text") {
      text += part.text ?? "";
    } else if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
      const name = part.type === "dynamic-tool" ? "tool" : part.type.slice("tool-".length);
      toolCalls.push({
        callId: part.toolCallId ?? nextCallId(),
        name,
        input: part.input,
        hasOutput: part.state === "output-available",
        output: part.output,
        ...(part.state === "output-error" ? { errorText: part.errorText ?? "" } : {}),
      });
    }
  }

  const outcome: TurnOutcome = !input.terminal ? "failed" : message.metadata?.interrupted ? "interrupted" : "complete";

  return buildTurn({
    id: message.id,
    role: message.role,
    reasoning: reasoning || undefined,
    text: text || undefined,
    toolCalls,
    outcome,
    usage: message.metadata?.usage,
  });
}

/**
 * A `Turn` → the AI SDK's `UIMessage` shape (MIN-292 review item 1): the one
 * rendering adapter both `readTranscript`'s callers and a thread's live
 * `Chat` share, so reloading a thread's history renders it exactly the way
 * the live stream would have — reasoning, tool activity, text, then
 * citations, the same order `buildTurn` assembles them in.
 *
 * Tool-call ids and source ids are regenerated here as `${turn.id}-tool-{i}`
 * / `src-{i}` rather than carrying a `ToolPart`'s/citation's own internal id
 * (`call-N` / `cite-N`, assigned by the shared module counters in this
 * file): those counters are for the package's own React consumers, not the
 * wire-compatible scheme a reloaded `UIMessage` must match so a rendered
 * turn is indistinguishable from one the live stream produced.
 */
export function turnToUiMessage(turn: Turn): UIMessage {
  let toolIndex = 0;
  let citationIndex = 0;

  const parts: UIMessage["parts"] = [];
  for (const part of turn.parts) {
    switch (part.kind) {
      case "reasoning":
        parts.push({ type: "reasoning", text: part.text } as UIMessage["parts"][number]);
        break;
      case "text":
        parts.push({ type: "text", text: part.text } as UIMessage["parts"][number]);
        break;
      case "tool": {
        const toolCallId = `${turn.id}-tool-${toolIndex}`;
        toolIndex += 1;
        if (part.state === "error") {
          parts.push({
            type: `tool-${part.name}`,
            toolCallId,
            state: "output-error",
            input: part.input,
            errorText: part.error,
          } as UIMessage["parts"][number]);
        } else if (part.state === "done") {
          parts.push({
            type: `tool-${part.name}`,
            toolCallId,
            state: "output-available",
            input: part.input,
            output: part.output,
          } as UIMessage["parts"][number]);
        } else {
          parts.push({
            type: `tool-${part.name}`,
            toolCallId,
            state: part.input !== undefined ? "input-available" : "input-streaming",
            ...(part.input !== undefined ? { input: part.input } : {}),
          } as UIMessage["parts"][number]);
        }
        break;
      }
      case "citation": {
        const sourceId = `src-${citationIndex}`;
        citationIndex += 1;
        const mindforge: Record<string, unknown> = {};
        if (part.excerpt !== undefined) mindforge.text = part.excerpt;
        if (part.score !== undefined) mindforge.score = part.score;
        parts.push({
          type: "source-document",
          sourceId,
          mediaType: "text/plain",
          title: part.title,
          filename: part.detail,
          ...(Object.keys(mindforge).length ? { providerMetadata: { mindforge } } : {}),
        } as UIMessage["parts"][number]);
        break;
      }
      case "opaque":
        // MIN-293: an opaque part must be reachable, not rejected — encoded as
        // an AI SDK "data-*" part (`type: "data-<renderAs>"`), the SDK's own
        // shape for host-defined data. `uiToBubbles` (`./elements/chat-turn`)
        // reads it back into `ChatBubble.opaque`, and `Thread`'s bubble→`Turn`
        // bridge turns it back into a `{ kind: "opaque" }` part reaching the
        // registry — so a reloaded thread's opaque part renders exactly like
        // a live one, never blocked here. `turnFromHistory`/`turnFromStream`
        // don't emit `opaque` today (no builder produces one yet), so this
        // path isn't exercised by either in practice, but it must not be a
        // trap for whichever host does first.
        parts.push({ type: `data-${part.renderAs}`, data: part.data } as UIMessage["parts"][number]);
        break;
    }
  }

  return {
    id: turn.id,
    role: turn.role,
    parts,
    ...(turn.outcome === "interrupted" ? { metadata: { interrupted: true } } : {}),
  } as UIMessage;
}
